import { combineReducers } from 'redux';
import { configureStore, createListenerMiddleware } from '@reduxjs/toolkit';
import { persistReducer, persistStore } from 'redux-persist';
import autoMergeLevel2 from 'redux-persist/lib/stateReconciler/autoMergeLevel2';
import storage from 'redux-persist/lib/storage';
import AuthStateReducer from './authentication/Reducer';
import SocketStateReducer from './socket/Reducer';
import BaseAppStateReducer from './baseapplication/Reducer';
import ProfileStateReducer from './profile/Reducer';
import CarriesAppStateReducer from './carriesapplication/Reducer';
import PersistAppStateReducer from './persistapplication/Reducer';
import { createReduxHistoryContext } from 'redux-first-history';
import storageSession from 'redux-persist/lib/storage/session';
import { createBrowserHistory } from 'history';
import {
    targetFormikListener,
    useFormikEffectListener,
    formikReducer,
} from 'shared/helpers/Formik';
import {
    createStateSyncMiddleware,
    initMessageListener,
} from 'redux-state-sync';

import {
    // eslint-disable-next-line import/no-named-default
    default as createSocketIoMiddleware,
    defaultBroadcast,
    defaultExecute,
} from '../worker';
import { SocketReduxActions } from './socket/Action';
import BroadcastStateReducer from './broadcast/Reducer';
import { BroadcastReduxActions } from './broadcast/Action';
import { AuthReduxActions } from './authentication/Action';

const { createReduxHistory, routerMiddleware, routerReducer } =
    createReduxHistoryContext({
        history: createBrowserHistory({ window }),
        savePreviousLocations: 1,
    });

const routePersistConfig = {
    key: 'router',
    storage: storageSession,
    blacklist: [],
};

const appReducer = combineReducers({
    router: persistReducer(routePersistConfig, routerReducer),
    authState: AuthStateReducer,
    baseAppState: BaseAppStateReducer,
    profileState: ProfileStateReducer,
    formikState: formikReducer,
    carryState: CarriesAppStateReducer,
    persistState: PersistAppStateReducer,
    socketState: SocketStateReducer,
    broadcastState: BroadcastStateReducer,
});

const Worky = globalThis.Worky.prototype.constructor;
const Worker = new Worky(
    `${window.location.href
        .split('/')
        .slice(0, 3)
        .join('/')}/js/signal/bundle.js`
);

const socketIoMiddleware = createSocketIoMiddleware(
    Worker,
    ['SOCKET_', 'BROADCAST_'],
    {
        eventName: [
            SocketReduxActions.TO_SOCKET_REDUX_WORKER,
            SocketReduxActions.TO_SOCKET_INITIAL_HANDSHAKE,
            BroadcastReduxActions.TO_BROADCAST_REDUX_WORKER,
            BroadcastReduxActions.TO_REMOVE_BROADCAST_REDUX_MESSAGE,
            BroadcastReduxActions.TO_CLONE_BROADCAST_REDUX_MESSAGE,
            BroadcastReduxActions.TO_BROADCAST_PROGRESSION_REDUX_MESSAGE,
            BroadcastReduxActions.TO_BROADCAST_PAGELEAVE_REDUX_MESSAGE,
            BroadcastReduxActions.TO_BROADCAST_AUTH_REDUX_MESSAGE,
            BroadcastReduxActions.TO_BROADCAST_ACTIVE_TAB_MESSAGE,
        ],
        execute: defaultExecute,
        executeBroadcast: defaultBroadcast,
    }
);

const rootReducer = (state: any | undefined, action: any): any => {
    if (action.type === 'LOGOUT' || action.type === 'RESET_STORE') {
        storage.removeItem('persist:perpetual');
        return appReducer(undefined, action);
    }
    return appReducer(state, action);
};

const persistConfig = {
    key: 'perpetual',
    storage,
    stateReconciler: autoMergeLevel2,
    blacklist: ['socket', 'carryState', 'router'],
};

const persistedReducer = persistReducer(persistConfig, rootReducer);
const listenerMiddleware = createListenerMiddleware();
listenerMiddleware.startListening({
    type: targetFormikListener,
    effect: useFormikEffectListener,
});

const store = configureStore({
    reducer: persistedReducer,
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({
            immutableCheck: false,
            serializableCheck: false,
            thunk: true,
        })
            .concat(
                createStateSyncMiddleware({
                    whitelist: [
                        AuthReduxActions.AUTH_SET_LOGIN_STATUS,
                        AuthReduxActions.RESET_LOGGING_IN,
                        AuthReduxActions.RESET_LOGOUT_FIELD,
                        AuthReduxActions.LOGOUT,
                        AuthReduxActions.SESION_EXPIRED_LOGOUT,
                        AuthReduxActions.AUTH_SET_UPDATE_STATUS,
                        AuthReduxActions.IDLECHECK_EXPIRED_LOGOUT,
                    ],
                    broadcastChannelOption: { type: 'localstorage' },
                }) as any
            )
            .concat(socketIoMiddleware)
            .concat(routerMiddleware)
            .prepend(listenerMiddleware.middleware),
    devTools: process.env.NODE_ENV !== 'production',
});
initMessageListener(store);
export const history = createReduxHistory(store);
export const persistor = persistStore(store);
export default store;
